import { useCallback } from "react";

import { Collapse, Radio, Skeleton } from "antd";
import styled from "styled-components";

import { SavedFilterModel } from "models/filter";

import SavedFilterDetails from "components/filter/SavedFilterDetails";

const { Panel } = Collapse;

interface SavedFilterCollapseSelectorProps {
  savedFilters: SavedFilterModel[];
  selectedFilter: SavedFilterModel | null;
  onSelectedFilterChanged: (filter: SavedFilterModel) => void;
  loading?: boolean;
}

const SavedFilterCollapseSelector = ({
  savedFilters,
  selectedFilter,
  onSelectedFilterChanged,
  loading = false
}: SavedFilterCollapseSelectorProps) => {
  // Use filter id if available, otherwise use index as key
  const getFilterKey = useCallback(
    (filter: SavedFilterModel) => filter.id || savedFilters.indexOf(filter).toString(),
    [savedFilters]
  );

  // Panel is only open when filter is selected - derive from selectedFilter
  const activeKey = selectedFilter ? [getFilterKey(selectedFilter)] : [];

  const handlePanelChange = useCallback(
    (keys: string | string[]) => {
      const keyArray = Array.isArray(keys) ? keys : [keys];
      const selectedKey = keyArray[0];
      const filter = savedFilters.find((f) => getFilterKey(f) === selectedKey);
      if (filter) {
        onSelectedFilterChanged(filter);
      }
    },
    [savedFilters, onSelectedFilterChanged, getFilterKey]
  );

  const renderPanelHeader = useCallback(
    (filter: SavedFilterModel) => {
      const isSelected = selectedFilter === filter || selectedFilter?.id === filter.id;

      return <Radio checked={isSelected}>{filter.name}</Radio>;
    },
    [selectedFilter]
  );

  const parseFilterJSON = useCallback((filterJSON: string) => {
    try {
      return JSON.parse(filterJSON);
    } catch {
      return null;
    }
  }, []);

  if (loading) {
    return (
      <SkeletonContainer>
        <Skeleton.Button active block size="large" />
        <Skeleton.Button active block size="large" />
      </SkeletonContainer>
    );
  }

  if (!savedFilters || savedFilters.length === 0) {
    return <EmptyText>No filters available</EmptyText>;
  }

  return (
    <StyledCollapse
      data-testid="default-filter-dropdown"
      activeKey={activeKey}
      onChange={handlePanelChange}
      accordion
      expandIcon={() => null}>
      {savedFilters.map((filter) => {
        const parsedFilter = parseFilterJSON(filter.filterJSON);
        return (
          <Panel header={renderPanelHeader(filter)} key={getFilterKey(filter)}>
            {parsedFilter ? (
              <SavedFilterDetails filter={parsedFilter} />
            ) : (
              <ErrorText>Unable to load filter details</ErrorText>
            )}
          </Panel>
        );
      })}
    </StyledCollapse>
  );
};

export default SavedFilterCollapseSelector;

const SkeletonContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;

  .ant-skeleton-button {
    opacity: 0.8;
  }
`;

const StyledCollapse = styled(Collapse)`
  border-radius: var(--border-radius);

  .ant-collapse-header:hover {
    background: #f5f5f5;
  }

  .ant-radio-wrapper {
    width: 100%;
    pointer-events: none; // Prevent radio from intercepting clicks
  }
`;

const EmptyText = styled.div`
  padding: 12px 16px;
  color: #999;
  border: 1px solid rgba(217, 217, 217, 0.6);
  border-radius: var(--border-radius);
  text-align: center;
`;

const ErrorText = styled.div`
  padding: 12px 16px;
  color: #ff4d4f;
  text-align: center;
  font-size: 14px;
`;
